import React, { ReactNode, useMemo } from 'react';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material';
import { useTenant } from '../contexts/TenantContext';

interface DynamicThemeProviderProps {
  children: ReactNode;
}

export const DynamicThemeProvider: React.FC<DynamicThemeProviderProps> = ({ children }) => {
  const { tenantConfig } = useTenant();

  const theme = useMemo(() => {
    // Create base theme with tenant-specific colors
    let dynamicTheme = createTheme({
      palette: {
        primary: {
          light: tenantConfig?.primary_color ? `${tenantConfig.primary_color}CC` : '#607d96',
          main: tenantConfig?.primary_color || '#3c5770',
          dark: tenantConfig?.primary_color ? `${tenantConfig.primary_color}DD` : '#2b3f50',
          contrastText: '#fff',
        },
        secondary: {
          light: tenantConfig?.secondary_color ? `${tenantConfig.secondary_color}CC` : '#e6c470',
          main: tenantConfig?.secondary_color || '#d4ad43',
          dark: tenantConfig?.secondary_color ? `${tenantConfig.secondary_color}DD` : '#b08c2c',
          contrastText: '#fff',
        },
        background: {
          default: '#f7f7f7',
          paper: '#ffffff',
        },
        text: {
          primary: '#333333',
          secondary: '#666666',
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
          fontWeight: 700,
          fontSize: '3rem',
          letterSpacing: '-0.01em',
        },
        h2: {
          fontWeight: 600,
          fontSize: '2.5rem',
          letterSpacing: '-0.01em',
        },
        h3: {
          fontWeight: 600,
          fontSize: '2rem',
          letterSpacing: '-0.01em',
        },
        h4: {
          fontWeight: 600,
          fontSize: '1.75rem',
        },
        h5: {
          fontWeight: 500,
          fontSize: '1.5rem',
        },
        h6: {
          fontWeight: 500,
          fontSize: '1.25rem',
        },
        body1: {
          fontSize: '1rem',
          lineHeight: 1.7,
        },
        button: {
          fontWeight: 500,
          textTransform: 'none',
        },
      },
      shape: {
        borderRadius: 4,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 4,
              padding: '10px 24px',
              transition: 'all 0.2s ease-in-out',
              fontSize: '0.95rem',
              fontWeight: 500,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              },
            },
            containedPrimary: {
              background: tenantConfig?.primary_color || '#3c5770',
            },
            containedSecondary: {
              background: tenantConfig?.secondary_color || '#d4ad43',
            },
            outlined: {
              borderWidth: '1.5px',
              '&:hover': {
                borderWidth: '1.5px',
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              overflow: 'visible',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 4,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            rounded: {
              borderRadius: 4,
            },
            elevation1: {
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
            },
            elevation2: {
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            },
          },
        },
        MuiContainer: {
          styleOverrides: {
            root: {
              paddingLeft: 16,
              paddingRight: 16,
              // Reduced from 32px to 16px for better spacing
            },
          },
        },
        MuiTypography: {
          styleOverrides: {
            gutterBottom: {
              marginBottom: '1em',
            },
          },
        },
      },
    });

    // Make typography responsive
    dynamicTheme = responsiveFontSizes(dynamicTheme);

    return dynamicTheme;
  }, [tenantConfig]);

  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  );
}; 